import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookOpen, ArrowRight, Sparkles, Search } from "lucide-react";
import { GUIDES } from "@/data/guides";

const TITLE = "US Visa Guides & F-1 Knowledge Hub | WeLivdIt";
const DESCRIPTION =
  "Free F-1 and B1/B2 visa guides: 214(b) refusals, home ties, funding logic and re-attempt strategy - built on 1,500+ WeLivdIt approvals.";
const CANONICAL = "https://welivdit.com/resources";

export const Route = createFileRoute("/resources/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: CANONICAL },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "US Visa Guides & F-1 Knowledge Hub",
          description: DESCRIPTION,
          url: CANONICAL,
          isPartOf: { "@type": "WebSite", name: "WeLivdIt", url: "https://welivdit.com" },
          mainEntity: {
            "@type": "ItemList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "F-1 Visa Knowledge Hub & Checklist",
                url: "https://welivdit.com/resources/f1-visa-guide",
              },
              ...GUIDES.map((g, i) => ({
                "@type": "ListItem",
                position: i + 2,
                name: g.title,
                url: `https://welivdit.com/resources/${g.slug}`,
              })),
            ],
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://welivdit.com" },
            { "@type": "ListItem", position: 2, name: "Resources", item: CANONICAL },
          ],
        }),
      },
    ],
  }),
  component: ResourcesIndex,
});

const CATEGORY_LABEL: Record<string, string> = {
  refusal: "Refusals & 214(b)",
  full: "Interview Prep",
  basic: "Visa Basics",
};

const CATEGORIES = [
  { key: "all", label: "All guides" },
  { key: "refusal", label: CATEGORY_LABEL.refusal },
  { key: "full", label: CATEGORY_LABEL.full },
  { key: "basic", label: CATEGORY_LABEL.basic },
] as const;

function ResourcesIndex() {
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState<(typeof CATEGORIES)[number]["key"]>("all");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return GUIDES.filter((g) => {
      if (category !== "all" && g.ctaPlan !== category) return false;
      if (!q) return true;
      return (
        g.title.toLowerCase().includes(q) ||
        g.description.toLowerCase().includes(q) ||
        g.toc.some((t) => t.heading.toLowerCase().includes(q)) ||
        g.faqs.some((f) => f.q.toLowerCase().includes(q))
      );
    });
  }, [query, category]);

  return (
    <div className="bg-background">
      <section className="mx-auto max-w-5xl px-5 pt-16 pb-10 md:pt-24">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/40 border border-border text-xs">
          <BookOpen className="h-3.5 w-3.5 text-primary" />
          <span className="text-foreground/70 uppercase tracking-wider">Resources</span>
        </div>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight leading-[1.05]">
          US Visa <em className="not-italic text-primary">Guides</em>
        </h1>
        <p className="mt-4 text-base md:text-lg text-foreground/70 max-w-2xl leading-relaxed">
          Free in-depth guides written by WeLivdIt Specialists. Everything we've learned from 1,500+ approvals and
          hundreds of refusal debriefs.
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-5 pb-20">
        <Link
          to="/resources/f1-visa-guide"
          className="group mb-8 block rounded-2xl border-2 border-primary/40 bg-primary/[0.05] p-6 transition hover:border-primary hover:shadow-soft md:p-8"
        >
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-primary-foreground">
            <Sparkles className="h-3 w-3" /> Start here
          </span>
          <h2 className="mt-3 font-display text-2xl md:text-3xl font-semibold group-hover:text-primary transition-colors">
            F-1 Visa Knowledge Hub &amp; Checklist
          </h2>
          <p className="mt-2 max-w-2xl text-sm md:text-base text-foreground/70 leading-relaxed">
            Essential F-1 rules, SEVIS &amp; DS-160 timelines, funding logic, interview psychology and 214(b) recovery
            strategies - plus a live Q&amp;A section where you can ask our Specialists your own doubt.
          </p>
          <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
            Read the hub &amp; ask your doubt <ArrowRight className="h-4 w-4" />
          </span>
        </Link>

        {/* Search + category filters */}
        <div className="mb-6 space-y-3">
          <label className="relative block">
            <span className="sr-only">Search guides</span>
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search guides — e.g. 214(b), home ties, DS-160…"
              className="h-12 w-full rounded-full border border-border bg-card pl-11 pr-4 text-sm outline-none transition focus:border-primary"
            />
          </label>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button
                key={c.key}
                type="button"
                onClick={() => setCategory(c.key)}
                className={
                  "rounded-full border px-4 py-2 text-xs font-semibold transition " +
                  (category === c.key
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card text-foreground/70 hover:border-primary/50")
                }
              >
                {c.label}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <p className="rounded-2xl border border-border bg-card p-8 text-center text-sm text-foreground/70">
            No guide matches that search. Try “214(b)”, “funding”, or ask our team directly in the{" "}
            <Link to="/resources/f1-visa-guide" className="font-semibold text-primary">
              Knowledge Hub Q&amp;A
            </Link>
            .
          </p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {filtered.map((g) => (
              <Link
                key={g.slug}
                to="/resources/$slug"
                params={{ slug: g.slug }}
                className="group rounded-2xl border border-border bg-card p-6 hover:border-primary/50 hover:shadow-soft transition"
              >
                <span className="text-[11px] font-bold uppercase tracking-wider text-primary">
                  {CATEGORY_LABEL[g.ctaPlan]}
                </span>
                <h2 className="mt-2 font-display text-xl font-semibold group-hover:text-primary transition-colors">
                  {g.title}
                </h2>
                <p className="mt-2 text-sm text-foreground/70 leading-relaxed">{g.description}</p>
                <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-primary">
                  Read the guide <ArrowRight className="h-4 w-4" />
                </span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
